import { gql } from '@apollo/client';
import _ from 'lodash';
import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  IComparatorApiServiceParameters,
  IGraphProvider,
  basicHomeProduct,
  basicHomeSupplier,
} from '../../..';
import { LegacyOrderResponse } from '../../../../../helpers';
import {
  HomeProduct,
  ProductSupplier,
  // ProductSupplierFeature,
  ProductsWithSuppliers,
} from '../../../graphProduct';
import {
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
} from '../../../home';
import {
  IProduct,
  QuoteResponse,
  basicHomeIProduct,
  getUspsBySellingPoints,
} from '../../../product';
import {
  calculateSavingsQuery,
  calculationInputSavingsQuery,
  orderSavingsQuery,
  productsWithSuppliersQuery,
  sendEmailToCustomerQuerySavings,
  sendInactivityEmailToCustomerQuerySavings,
} from '../../queries';
import getSavingsFeaturesQuery from '../../queries/os-savings/getSavingsFeaturesQuery';
import { ApolloProvider } from '../apolloProvider';
import { validateAddressCommon } from '../commonGraphQLAPI';
import { SavingsQueryProvider } from './savingsQueryProvider';
import {
  CommonTechnical,
  FeaturesConnection,
  FeaturesEdge,
  TriggerEventPayload,
} from './types';

export class GraphSavingsApi extends AbstractComparatorApiService {
  private client;
  private queryProvider: SavingsQueryProvider;
  private readonly graphProvider: IGraphProvider;
  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    this.queryProvider = new SavingsQueryProvider();
    this.graphProvider = new ApolloProvider(params);
  }

  private generateUUID(): string {
    let d = new Date().getTime(); //Timestamp
    let d2 =
      (typeof performance !== 'undefined' &&
        performance.now &&
        performance.now() * 1000) ||
      0; //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = self.crypto.getRandomValues(new Uint32Array(1))[0] % 17;
        if (d > 0) {
          //Use timestamp until depleted
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
  }

  private getIdsFromProducts(products: HomeProduct[]): {
    parentProductIds: string[];
    productIds: string[];
  } {
    let parentProductIds: string[] = [];
    let productIds: string[] = [];

    for (const product of products) {
      if (product.dataProviderKrn.includes('moneyview')) {
        if (product.productType === 'insurance/package') {
          parentProductIds.push(product.externalId);
        } else {
          productIds.push(product.externalId);
        }
      }
    }

    return {
      parentProductIds,
      productIds,
    };
  }

  setClient(client) {
    this.client = client;
  }

  getClient() {
    return this.client;
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }

  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> {
    console.log(swiftUserId, swiftProductId, productType, accessToken);
    throw new Error('Method not implemented');
  }

  validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse> {
    const variables = this.queryProvider.validateAddressVariables(
      postcode,
      parseInt(houseNumber),
      houseNumberAddition
    );

    return validateAddressCommon(this.graphProvider, variables);
  }

  getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData> {
    console.log('getAddressData', postalCode, houseNumber, houseNumberAddition);
    throw new Error('Method not implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): any {
    console.log('getHomeFeaturesDetails', productId, after);
    throw new Error('getHomeFeaturesDetails method not implemented.');
  }

  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    console.log('calculateHomeContentPackage', calculateParameters);
    throw new Error('Method not implemented.');
  }

  buildCharacteristics = (features, type, name, productDescription) => {
    const sortOrder =
      type === 'V_DOV'
        ? [
            'Officiële aanbiedernaam',
            'Depositogarantiestelsel',
            'Bronbelasting 0%',
            'Bankvergunning',
            'Overige kosten',
            'Identificatie bij openen',
            'Bankpas',
            'Rentebijschrijving',
          ]
        : [
            'Officiële aanbiedernaam',
            'Depositogarantiestelsel',
            'Bronbelasting 0%',
            'Bankvergunning',
            'Bijkomende kosten',
            // "Vervroegd beëindigen",
          ];

    let tooltipMapping = {
      Garantiestelsel:
        'Als de bank onder het depositogarantiestelsel valt van Nederland of een ander EU-land, krijg je gegarandeerd maximaal €100.000 van het ingelegde geld terug als de bank failliet gaat.',
      'Bronbelasting 0%':
        'Een bronbelastingpercentage van 0% betekent dat er geen belasting wordt ingehouden op het verdiende geld in een bepaald land.',
      'Overige kosten':
        'Dit zijn de kosten die de bank in rekening brengt voor het beheer van de rekening.',
      'Identificatie bij openen':
        'Bij het openen van een rekening laat je aan de bank weten wie je bent met officiële documenten, zoals een identiteitsbewijs.',
      Bankpas:
        'Een bankpas gekoppeld aan je spaarrekening maakt geldopnames bij geldautomaten (online) betalingen mogelijk.',
      Rentebijschrijving:
        'Rentebijschrijving is wanneer de bank rente aan je rekeningsaldo toevoegt, meestal periodiek zoals maandelijks of jaarlijks, afhankelijk van de spaarrekeningvoorwaarden.',
    };

    const convertValue = (value) => {
      if (Number(value) === 1) return 'Ja';
      if (Number(value) === 0) return 'Nee';
      return _.capitalize(value);
    };

    const getIndex = (name) => sortOrder.indexOf(name);

    let characteristics = features
      .filter((feature) => sortOrder.includes(feature.node?.name))
      .sort((a, b) => getIndex(a.node?.name) - getIndex(b.node?.name))
      .map((feature) => ({
        label: feature.node?.name,
        value:
          feature.node?.name === 'Depositogarantiestelsel'
            ? this.getGarantiestelselMapping(feature.node?.data)
            : convertValue(feature.node?.data),
      }));

    if (characteristics.length > 0) {
      if (type !== 'V_DOV') {
        characteristics.splice(0, 0, {
          label: 'Aanbiedernaam',
          value: name.split(' - ')[0],
        });
      }
      characteristics.splice(1, 0, {
        label: 'Product',
        value: productDescription,
      });

      characteristics.forEach((item) => {
        if (item.label === 'Officiële aanbiedernaam') {
          item.label = 'Aanbiedernaam';
        } else if (item.label === 'Depositogarantiestelsel') {
          item.label = 'Garantiestelsel';
        }
        // Add tooltip based on label, if it exists in the mapping
        item.tooltip = tooltipMapping[item.label] || undefined;
      });
    }

    return characteristics || [];
  };

  getGarantiestelselMapping = (value) => {
    const conversions = {
      Nederlandse: 'Nederland',
      Duitse: 'Duitsland',
      Ierse: 'Ierland',
      Zweedse: 'Zweden',
      Spaanse: 'Spanje',
      Franse: 'Frankrijk',
      Estse: 'Estland',
      Belgische: 'België',
      Italiaanse: 'Italië',
      Slowaakse: 'Slowakije',
      Finse: 'Finland',
      Maltese: 'Malta',
      Luxemburgse: 'Luxemburg',
      Tsjechische: 'Tsjechië',
      Poolse: 'Polen',
      Letse: 'Letland',
      Portugese: 'Portugal',
      Litouwse: 'Litouwen',
      Noorse: 'Noorwegen',
      'Duitse | Ierse': 'Duitsland | Ierland',
    };

    return conversions?.[value] || value;
  };

  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity: boolean
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const variables = this.queryProvider.validateEmailVariables(
        comparisonId,
        email,
        productType
      );

      const query = inactivity
        ? sendInactivityEmailToCustomerQuerySavings
        : sendEmailToCustomerQuerySavings;

      const newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`;

      this.graphProvider
        .mutate<TriggerEventPayload>(
          gql(query),
          variables,
          'triggerEvent',
          undefined,
          newClientLink
        )
        .then((data: TriggerEventPayload) => {
          if (data.success) {
            resolve('Email sent successfully');
          } else {
            reject('Email could not be sent');
          }
        })
        .catch((err) => {
          console.error(err);
          reject('Email could not be sent');
        });
    });
  }

  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    console.log('getCalculationInput', transactionReference);
    throw new Error('Method not implemented.');
  }

  orderStatus(orderId: string): Promise<OrderStatusResult> {
    console.log('orderStatus', orderId);
    throw new Error('Method not implemented.');
  }

  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']> {
    console.log('getOrderDefinition', product, productType);
    throw new Error('Method not implemented.');
  }

  getComparison(id: string): Promise<Comparison> {
    const newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/savings/graphql`;
    return new Promise<Comparison>((resolve, reject) => {
      this.graphProvider
        .query<any>(
          gql(calculationInputSavingsQuery),
          { comparisonId: id },
          'calculationData',
          undefined,
          newClientLink
        )
        .then(async (data: any) => {
          if (data.calculationInput) {
            const productSavingType =
              data.calculationInput.policyDetails[0].moneyViewSavings[0]
                .type === 'VDov'
                ? 'V_DOV'
                : 'V_DPS';

            const savings =
              data.calculationInput.policyDetails[0].moneyViewSavings[0]
                .moneyViewInterestCollections[0].moneyViewInterests[0].value;

            const duration =
              data.calculationInput.policyDetails[0].moneyViewSavings[0]
                .moneyViewInterestCollections[0].moneyViewInterests[0]
                ?.duration || '';

            const criteria = {
              criteria: {
                savings,
                productSavingType,
                duration,
              },
            };

            resolve(criteria);
          } else {
            reject('Incorrect Response!');
          }
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  }

  async handleFeatures(
    parentProductIds: string[],
    productIds: string[],
    after: string | undefined,
    alreadyLoadedFeatures: FeaturesEdge[] = []
  ): Promise<FeaturesEdge[]> {
    let tempFeatures: FeaturesEdge[] = alreadyLoadedFeatures;

    const featuresQueryVariables = this.queryProvider.featuresVariables(
      parentProductIds,
      productIds,
      after
    );

    return new Promise<FeaturesEdge[]>((resolve, reject) => {
      this.graphProvider
        .query<FeaturesConnection>(
          gql(getSavingsFeaturesQuery),
          featuresQueryVariables,
          'features',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/features/graphql`
        )
        .then((feature: FeaturesConnection) => {
          const edges = feature.edges ?? [];
          if (feature.pageInfo.hasNextPage) {
            resolve(
              this.handleFeatures(
                parentProductIds,
                productIds,
                feature.pageInfo.endCursor as string,
                [...tempFeatures, ...edges]
              )
            );
          } else {
            resolve([...tempFeatures, ...edges]);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getProducts(
    searchCriteria: object,
    selectedFilters: object
  ): Promise<IProduct[]> {
    let newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/savings/graphql`;
    return new Promise<IProduct[]>((resolve, reject) => {
      const variables = this.queryProvider.validateSavingsVariables(
        searchCriteria,
        selectedFilters,
        this.credentials,
        this.generateUUID()
      );
      let features: FeaturesEdge[] = [];
      let products: HomeProduct[] = [];
      let suppliers: ProductSupplier[] = [];
      let commonTechnical: CommonTechnical[];
      this.graphProvider
        .query<any>(
          gql(calculateSavingsQuery),
          variables,
          'calculateSavings',
          undefined,
          newClientLink
        )
        .then((data) => {
          if (data.policyDetails) {
            const productsWithSuppliersQueryVars =
              this.queryProvider.validateSuppliersVariables(data);

            if (Array.isArray(data?.commonTechnical)) {
              commonTechnical = data.commonTechnical as CommonTechnical[];
            }

            this.graphProvider
              .query<ProductsWithSuppliers>(
                gql(productsWithSuppliersQuery),
                productsWithSuppliersQueryVars,
                'productsWithSuppliers',
                'network-only',
                `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`
              )
              .then(async (productsWithSuppliers) => {
                products = productsWithSuppliers?.products;
                suppliers = productsWithSuppliers?.suppliers;

                const { parentProductIds, productIds } =
                  this.getIdsFromProducts(products);
                features = await this.handleFeatures(
                  parentProductIds,
                  productIds,
                  undefined
                );

                const formatted = this.formatSavingsProduct(
                  this.credentials,
                  data.policyDetails,
                  products,
                  suppliers,
                  features,
                  commonTechnical,
                  variables
                );
                resolve(formatted);
              })
              .catch((error) => {
                reject(error);
              });
          } else {
            reject('No products returned');
          }
        })
        .catch((error) => {
          console.log(error);
          reject('No products returned');
        });
    });
  }

  formatSavingsProduct(
    credentials: any,
    packages: any[],
    products: HomeProduct[],
    suppliers: ProductSupplier[],
    featuresEdges: FeaturesEdge[],
    commonTechnical: CommonTechnical[],
    variables: any
  ): IProduct[] {
    const minInvestmentAmount =
      variables.input.policyDetails.moneyViewSavings
        .moneyViewInterestCollections.moneyViewInterests.value;
    return packages.map((policyDetails) => {
      const {
        // collectivityDescription,
        customProperties,
        moneyViewSavings,
        productCode,
        productDescription,
        // refKey,
      } = policyDetails;

      const product: HomeProduct = {
        ...basicHomeProduct,
        ...products.find((p) => p.krn === productCode),
      };

      const supplier: ProductSupplier = {
        ...basicHomeSupplier,
        ...suppliers.find((s) => s.krn === customProperties?.supplierKrn),
      };

      const {
        affiliateUrl,
        // correspondingMoneyViewExternalId,
        // customAttributes,
        // dataProviderKrn,
        // description,
        // entityType,
        // externalId,
        // id,
        insuranceCardUrl,
        krn,
        name,
        orderType,
        // productCategory,
        // productIncentives,
        // productSupplierId,
        productType,
        // productTypeKrn,
        // refKey,
        sellingPoints,
        // state,
        termsAndConditionsUrl,
        testScores,
        // version,
      } = product;

      const {
        about,
        // addresses,
        advantages,
        disadvantages,
        // entityType,
        // externalIds,
        // features,
        howItWorks,
        // id,
        // informationContacts,
        // krn,
        // legalName,
        name: providerName,
        nameLogoUrl,
        // porCompany,
        // refKey,
        // state,
        sustainabilityScore,
        taskDivisions,
        // version,
      } = supplier;

      const productId = parseInt(product.externalId);
      const ownFeatures = featuresEdges.filter((feature) => {
        return [feature.node.parentProductId, feature.node.productId].includes(
          productId
        );
      });

      let visibleAffiliateUrl = '';

      if (orderType === 'AFFILIATE') {
        visibleAffiliateUrl = affiliateUrl;
      }

      return {
        ...basicHomeIProduct,
        affiliateUrl: visibleAffiliateUrl,
        commonTechnical: [
          {
            senderId: credentials.clientId,
            sessionId: '',
            transactionReference:
              commonTechnical?.[0]?.transactionReference || '',
          },
        ],
        bigProductFeature: {
          ...basicHomeIProduct.bigProductFeature,
          decimals: 0,
          label: 'Eigen risico',
          hideIfZero: true,
          variant: 'priceMedium',
          value: {
            month: 0,
            year: 0,
          },
        },
        minInvestmentAmount,
        characteristics: this.buildCharacteristics(
          ownFeatures || [],
          moneyViewSavings?.[0]?.type,
          name,
          productDescription
        ),
        fees: ownFeatures.map(({ node }) => {
          return {
            category: node.category,
            label: node.name,
            value: node.data,
          };
        }),
        id: `${productCode}`,
        image: nameLogoUrl,
        orderType,
        priceInfo: {
          ...basicHomeIProduct.priceInfo,
          initial: Number(
            moneyViewSavings?.[0]?.moneyViewInterestCollections?.[0]?.moneyViewInterests?.[0]?.value?.replace(
              ',',
              '.'
            )
          ),
          total: Number(
            moneyViewSavings?.[0]?.moneyViewInterestCollections?.[0]?.moneyViewInterests?.[0]?.value?.replace(
              ',',
              '.'
            )
          ),
        },
        productCode: productCode || '',
        productType: productType || '',
        termsAndConditionsUrl,
        testScores,
        prosAndCons: {
          cons: disadvantages?.map((el) => el.description),
          pros: advantages?.map((el) => el.description),
        },
        provider: {
          ...basicHomeIProduct.provider,
          description: about,
          howItWorks,
          insuranceCardUrl,
          label: name + ' ' + productDescription,
          name: providerName,
          sellingPoints,
          sustainabilityScore: sustainabilityScore
            ? `${sustainabilityScore[0].score}`
            : '',
          taskDivisions,
          termsAndConditionsUrl,
          id: krn,
        },
        purchasableProduct: orderType !== 'NOT_ORDERABLE',
        testScore: testScores?.[0]?.score || 0,
        usps: getUspsBySellingPoints(sellingPoints),
        subscriptionName: name || '',
        type: 'savings',
        policyPolicyDetails: [policyDetails],
        supplierName: name,
        productSavingType: moneyViewSavings?.[0]?.type,
        garantiestelselNederland:
          ownFeatures.find(
            (feature) => feature.node.name === 'Depositogarantiestelsel'
          )?.node?.data === 'Nederlandse',
        garantiestelsel: this.getGarantiestelselMapping(
          ownFeatures.find(
            (feature) => feature.node.name === 'Depositogarantiestelsel'
          )?.node?.data
        ),
        duration:
          moneyViewSavings?.[0]?.moneyViewInterestCollections?.[0]
            ?.moneyViewInterests?.[0]?.duration,
      };
    });
  }

  async orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string
  ): Promise<OrderResult> {
    const variables = this.queryProvider.orderSavingsVariables(
      transactionReference,
      productCode,
      this.credentials.clientId ?? ''
    );
    const query = orderSavingsQuery;

    return new Promise<OrderResult>((resolve, reject) => {
      this.graphProvider
        .mutate<{ orderId: string }>(
          query,
          variables,
          'orderSavings',
          undefined,
          `${process.env.KIZI_PUBLIC_DOMAIN}/savings/graphql`
        )
        .then((data: { orderId: string }) => {
          resolve({
            orderId: data?.orderId,
            errors: [],
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  checkMembershipStatus(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }
}
